import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Note } from '../components/ui/Note';
import { Panel } from '../components/ui/Panel';
import { useDocumentHead } from '../hooks/useDocumentHead';

/**
 * Sign-in and signup are the same form. `POST /auth/request` creates a free
 * account for an unrecognised address rather than refusing it, see the
 * handler's comment on why that's deliberate, so there's no separate signup
 * flow to build.
 */
export function SignInPage() {
    useDocumentHead({
        title: 'Sign in · ActualSold',
        description: 'Magic-link sign in. Free tier, no card.',
        canonicalPath: '/signin',
        noindex: true,
    });

    const [params] = useSearchParams();
    const [email, setEmail] = useState('');

    const linkExpired = params.get('reason') === 'link_expired';

    const request = useMutation({ mutationFn: () => api.requestLink(email.trim()) });

    if (request.isSuccess) {
        return (
            <Panel title="Check your email">
                <p className="text-sm text-text-muted">
                    If <span className="text-text">{email.trim()}</span> can receive a link, it's on its way. It works
                    once and expires in 15 minutes.
                </p>

                {request.data.devLink ? (
                    <div className="mt-4 space-y-2">
                        <Note>
                            No mail provider is configured, so here's the link directly. This only ever happens in
                            development.
                        </Note>
                        <a
                            href={request.data.devLink}
                            className="block font-mono text-xs break-all text-primary underline"
                        >
                            {request.data.devLink}
                        </a>
                    </div>
                ) : null}
            </Panel>
        );
    }

    return (
        <Panel title="Sign in">
            {linkExpired ? (
                <div className="mb-5">
                    <Note tone="warn">
                        That sign-in link expired or was already used. Links work once and last 15 minutes —
                        enter your email for a fresh one.
                    </Note>
                </div>
            ) : null}

            <p className="mb-5 text-sm text-text-muted">
                No password. Enter your email and we'll send a link that signs you in. New addresses get a free
                account automatically, 100 requests a month, no card required.
            </p>

            <form
                className="flex flex-wrap items-end gap-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (email.trim()) request.mutate();
                }}
            >
                <div className="min-w-64 flex-1">
                    <Input
                        label="Email"
                        type="email"
                        value={email}
                        onChange={setEmail}
                        placeholder="you@example.com"
                        autoFocus
                    />
                </div>
                <Button type="submit" disabled={request.isPending || !email.trim()}>
                    {request.isPending ? 'Sending…' : 'Email me a link'}
                </Button>
            </form>

            {request.error ? (
                <div className="mt-4">
                    <Note tone="warn">
                        {request.error instanceof ApiError ? request.error.message : 'Something went wrong.'}
                    </Note>
                </div>
            ) : null}
        </Panel>
    );
}
